import { CuriosityNamespaceType, CuriosityValue, type CuriosityTranslation } from '@procureai/curiosity-ui-mui';
import { TextAreaField } from '@procureai/ui-form-mui/fields';
import { Check, Pencil, Trash, X } from 'lucide-react';
import { isNotNil } from 'ramda';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { Button } from '@/shared/components/ui/button';
import { ButtonGroup } from '@/shared/components/ui/button-group';
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/shared/components/ui/item';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useRelativeTimeFormat } from '~/hooks/useRelativeTimeFormat/useRelativeTimeFormat';
import { initials } from '~/util/string';

interface UserEntity {
  firstName?: string;
  lastName?: string;
  email?: string;
}

const getFullName = (firstName?: string, lastName?: string, email?: string): string | undefined => {
  const parts = [firstName, lastName].filter(isNotNil);
  return parts.length > 0 ? parts.join(' ') : email || undefined;
};

export interface Comment {
  id: string;
  authorId: string;
  text: string;
  timestamp: string;
  canEdit?: boolean;
  canDelete?: boolean;
}

interface CommentsItemProps {
  comment: Comment;
  onEdit: (id: string, newText: string) => void;
  onDelete: (id: string) => void;
}

// Small components that render author info with fallback to authorId
const AuthorInitials = ({ authorId }: { authorId: string }) => (
  <CuriosityValue
    ns={CuriosityNamespaceType.INTERNAL_USER}
    i18nKey={authorId}
    valueGetter={(val) => {
      const user = val as CuriosityTranslation<UserEntity>;
      const { firstName, lastName } = user?.entity ?? {};
      return initials([firstName, lastName]);
    }}
  ></CuriosityValue>
);

const AuthorName = ({ authorId }: { authorId: string }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Comments' });
  return (
    <CuriosityValue
      ns={CuriosityNamespaceType.INTERNAL_USER}
      i18nKey={authorId}
      valueGetter={(val) => {
        const user = val as CuriosityTranslation<UserEntity>;
        const { firstName, lastName, email } = user?.entity ?? {};
        return getFullName(firstName, lastName, email);
      }}
      fallback={t('unknownUser')}
    ></CuriosityValue>
  );
};

export const CommentsItem = ({ comment, onEdit, onDelete }: CommentsItemProps) => {
  const formatTimestamp = useRelativeTimeFormat();
  const { t } = useTranslation(undefined, { keyPrefix: 'Comments' });

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState<Union.Nullable<string>>(comment.text);

  const handleStartEdit = () => {
    setEditText(comment.text);
    setIsEditing(true);
  };

  const handleDelete = () => {
    onDelete(comment.id);
  };

  const handleCancelEdit = () => {
    setEditText(comment.text);
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    const trimmedText = editText?.trim() ?? '';
    if (trimmedText) {
      onEdit(comment.id, trimmedText);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const canShowEdit = comment.canEdit;
  const canShowDelete = comment.canDelete;
  const showActionButtons = canShowEdit || canShowDelete;

  return (
    <Item size="sm" className="w-full !pr-0 !pl-0 group">
      <ItemMedia>
        <div className="bg-muted text-muted-foreground flex size-9 items-center justify-center rounded-full text-sm font-medium">
          <AuthorInitials authorId={comment.authorId} />
        </div>
      </ItemMedia>
      <ItemContent className="min-w-0 flex-1">
        <ItemTitle>
          <span>
            <AuthorName authorId={comment.authorId} />
            <Tooltip>
              <TooltipTrigger
                render={
                  <span className="text-muted-foreground cursor-default text-xs font-normal ml-1">
                    {formatTimestamp(comment.timestamp)}
                  </span>
                }
              />
              <TooltipContent side="top">{new Date(comment.timestamp).toLocaleString()}</TooltipContent>
            </Tooltip>
          </span>
        </ItemTitle>
        {isEditing ? (
          <TextAreaField value={editText ?? ''} onChange={setEditText} onKeyDown={handleKeyDown} autoFocus />
        ) : (
          <ItemDescription className="line-clamp-none whitespace-normal text-wrap">{comment.text}</ItemDescription>
        )}
      </ItemContent>
      {showActionButtons && !isEditing && (
        <ItemActions className="group-hover:opacity-100 opacity-0 transition-opacity right-0">
          <ButtonGroup>
            {canShowEdit && (
              <Button variant="ghost" size="icon" onClick={handleStartEdit} aria-label={t('edit')} className="size-7">
                <Pencil className="size-3.5" />
              </Button>
            )}
            {canShowDelete && (
              <Button variant="ghost" size="icon" onClick={handleDelete} aria-label={t('delete')} className="size-7">
                <Trash className="size-3.5" />
              </Button>
            )}
          </ButtonGroup>
        </ItemActions>
      )}
      {isEditing && (
        <ButtonGroup>
          <Button variant="ghost" size="icon" onClick={handleSaveEdit} aria-label={t('saveEdit')} className="size-8">
            <Check className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancelEdit}
            aria-label={t('cancelEdit')}
            className="size-8"
          >
            <X className="size-4" />
          </Button>
        </ButtonGroup>
      )}
    </Item>
  );
};
